// Story 7.1 (ADO-904) — Catalogue browse read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// SearchAndDiscovery browse read path (three-level Department › Category ›
// Sub-Category hierarchy + a node's product listing). Mirrors the
// `profile.client.ts` / `tenant-context.client.ts` seam pattern: it is the
// SINGLE consumer seam until the `openapi-typescript`-generated client lands
// (Story 6.1 deviation D-1), at which point these calls move to the generated
// surface.
//
// SCOPE NOTE: the wire shapes here carry the taxonomy nodes and a product's
// name / description / pack size / unit of measure (Story 7.1) plus its primary
// Brand Bank image URL (Story 7.4). Price + Add (Epic 8 / pricing), stock (7.3),
// favourites (7.5) and attribute filters (7.10) remain absent seams; those
// stories extend `ProductSummary` and add their own client seams. The backend
// read API (GetCategoryHierarchy / ListProductsByNode) is live, and the
// `/api/v1` global prefix landed repo-wide (ADO-1181, closing Story 6.1 D-1's
// prefix half); these calls are still exercised against msw in tests.

import { API_PREFIX, apiFetch } from './shoplink-api';
import { ENTRY_PARAM, appendCustomerAccountParam, withEntry } from './catalogue-entry';
import {
  appendFilterParams,
  type CatalogueNarrowing,
  type Facet,
  type ProductFilters,
} from './facets';
import type { MoneyValue } from '../shared/money';

export type { Facet, FacetValue, FacetKey, ProductFilters } from './facets';

/** The three fixed levels of the catalogue taxonomy (DD-C.4; app-enforced depth). */
export type CategoryLevel = 'DEPARTMENT' | 'CATEGORY' | 'SUBCATEGORY';

/**
 * Story 7.30 (ADO-1112), AC2: the offer kinds a product can carry. The same five the
 * `offerType`/`promotionalStatus` facets filter on. Lock-step with `OfferKindDto`.
 */
export type OfferKind =
  | 'PROMOTION'
  | 'MULTI_BUY'
  | 'LEAFLET_LINE'
  | 'WOW'
  | 'RBP'
  /** Story 7.32 (ADO-1115): a line BWG is backing this period. Only the collected-offers
   *  listing and its `offerType=FEATURED_LINE` filter resolve it. */
  | 'FEATURED_LINE';

/**
 * Story 7.32 (ADO-1115), AC1: what BWG has said about a Featured Line and what the Trading
 * Account will be charged for it: the pricing authority's figure for that account, never the
 * provisional stand-in. Lock-step with `FeaturedLineDto`.
 */
export interface FeaturedLine {
  readonly headline: string;
  readonly copy: string | null;
  readonly unitPrice: MoneyValue;
}

/** A node in the Department › Category › Sub-Category tree. */
export interface CategoryNode {
  readonly id: string;
  readonly name: string;
  readonly level: CategoryLevel;
  /**
   * Whether the node has child nodes below it. A DEPARTMENT/CATEGORY with
   * children drills down; a SUBCATEGORY (or a childless node) is a leaf whose
   * selection opens the product listing.
   */
  readonly hasChildren: boolean;
}

/** The children of a parent node (or the top-level Departments when parent is null). */
export interface CategoryChildren {
  /** The parent whose children these are; null for the top-level Department list. */
  readonly parent: CategoryNode | null;
  readonly children: readonly CategoryNode[];
}

/**
 * A product as it appears in a browse listing. Story 7.1 fields — name,
 * description, pack size, unit of measure (epics.md Story 7.1 AC2) — plus the
 * primary Brand Bank image URL (Story 7.4). Sibling stories add price / stock /
 * favourite state. Lock-step with `ProductSummaryDto` in the backend
 * `search-and-discovery` contracts — both gain `imageUrl: string | null`.
 */
export interface ProductSummary {
  readonly id: string;
  readonly sku: string;
  readonly name: string;
  readonly description: string | null;
  readonly packSize: string | null;
  readonly unitOfMeasure: string | null;
  /** ADO-2249 (BWG feedback, 5 Nov): the retail units a case makes (SL1.0 `RetailUnits`), as
   *  text; absent when the listing holds none. The pack shows as `{retailUnits} / {packSize}`
   *  (`packCaseLabel`). Lock-step with `ProductSummaryDto`. */
  readonly retailUnits?: string;
  /** Story 7.4 (ADO-908): absolute CDN URL of the primary product image, or
   *  `null` when there is no reference → the card renders the branded
   *  placeholder (AC2), never a broken image. */
  readonly imageUrl: string | null;
  /** Story 7.30 (ADO-1112), AC2: the offer kinds the caller can see on this
   *  product, deduplicated and in the server's declared vocabulary order. The card renders a marker
   *  per kind, so a Leaflet Line is identifiable inside the leaflet collection
   *  and anywhere else in the caller's Range, on the same basis. Empty when the
   *  product carries none. Lock-step with `ProductSummaryDto.offerKinds`, which
   *  always sends it; OPTIONAL here only so the many pre-existing product
   *  fixtures stay valid, and absent is read as "no marker" (never as unknown). */
  readonly offerKinds?: readonly OfferKind[];
  /** Picked fresh: no stock state, always orderable (Enabler E7-B, ADO-2273; MP AVL-5.4). The
   *  availability read leaves it out, so the card shows SL1.0's "Picked fresh" where the stock
   *  pill would be (Enabler E7-C, ADO-2274). Lock-step with the backend DTO. */
  readonly isPickedFresh: boolean;
  /** How the product shows in the caller's RBP core set: `CORE` ("Core") or `LINK` ("RBP - Link",
   *  a linkage child of a core product); absent when it is not core for the caller. One mark per
   *  product. Lock-step with the backend DTO. */
  readonly rbpMark?: 'CORE' | 'LINK';

  /** Whether the product offers its RBP links: `OFFERED` (an off-sale core parent, shown "not
   *  available") or `IF_OUT_OF_STOCK` (on sale; the card offers them only when priced and out of
   *  stock or unavailable, `showsRbpLinks`). Absent: no links. Lock-step with the backend DTO. */
  readonly rbpLinks?: RbpLinksOffer;
  /** Story 7.32 (ADO-1115): present only on a collected-offers listing's Featured Lines. */
  readonly featuredLine?: FeaturedLine;
}

/** See `ProductSummary.rbpLinks`. */
export type RbpLinksOffer = 'OFFERED' | 'IF_OUT_OF_STOCK';

/**
 * Story 7.15 (ADO-1372) AC1/AC2 — ONE product with its PDP-only detail fields and
 * its category breadcrumb, as returned by `GET /catalogue/products/:sku`.
 *
 * EXTENDS `ProductSummary` rather than widening it (backend Deviation D-4): these
 * fields would otherwise ride every grid-card row of every listing and search
 * response. Lock-step with `ProductDetailDto` in the backend `search-and-discovery`
 * contracts — this file's own standing rule.
 */
export interface ProductDetail extends ProductSummary {
  /** ADO-3693 (AC6): the high-resolution copy of the image `imageUrl` names, for the product
   *  page's hero and zoom only (no other surface reads it, AC4). `null` when the api has no HD
   *  root or the product has no small image. Optional because the web and the api roll
   *  separately, so an api without the field still parses: absent means `null`. Lock-step with
   *  `ProductDetailDto.hdImageUrl`. */
  readonly hdImageUrl?: string | null;
  /** The PRIMARY barcode (SL1.0 `EAN1`). `null` ⇒ the row is omitted entirely
   *  (AC3 — blank/hidden, never an error), never rendered as an empty value. */
  readonly ean: string | null;
  /** Country of origin as the feed's RAW code. Resolved to a country NAME for
   *  display, falling back to rendering this value VERBATIM when it does not
   *  resolve (D-7) — so an unrecognised code still shows something truthful. */
  readonly origin: string | null;
  /** The VAT MULTIPLIER as a decimal string: `'1.23'` means 23% VAT (D-8). The
   *  displayed percentage is derived as `(rate - 1) * 100`, mirroring SL1.0.
   *
   *  ⚠️ Guard on `=== null`, NEVER on falsiness: `'1'` is a genuine ZERO-RATED
   *  product and must render `0%`, not disappear. */
  readonly vatRate: string | null;
  /** Story 7.21 (ADO-1673) AC1/AC3 — SHELF LIFE in whole days (SL1.0 `Shelfdays`).
   *  A plain `number | null` (an `Int?` column, no Decimal/string dance). `null` ⇒
   *  the row is OMITTED (AC4); a present value renders as `"N days"` (AC3). */
  readonly shelfLifeDays: number | null;
  /** Story 7.21 (ADO-1673) AC2 — the SUPPLIER CODE. The fallback shown where no
   *  supplier NAME was carried (RW / Central Billing extracts). */
  readonly supplierCode: string | null;
  /** Story 7.21 (ADO-1673) AC2 — the SUPPLIER NAME, feed-carried and present only for
   *  WOW-extract products (14.17 D-2). Rendered where present; otherwise the code is
   *  shown. `ProductInformation` picks `supplierName ?? supplierCode` — never a
   *  cross-module resolution at read time (NFR37). */
  readonly supplierName: string | null;
  /** Story 7.21 (ADO-1673) AC3 — the ORDER INCREASE in whole days (WOW extract only).
   *  `number | null`; `null` ⇒ the row is omitted (AC4), else it renders `"N days"`. */
  readonly orderIncreaseDays: number | null;
  /** AC2 — Department › Category › Sub-Category, root-first. Real `CategoryNode`s,
   *  so the PDP's crumbs are NAVIGABLE like the listing's. Empty for an
   *  uncategorised product (the trail is then just "Shop › product name"), and
   *  partially populated when a path id no longer resolves — never an error. */
  readonly breadcrumb: readonly CategoryNode[];
}

/** Story 7-FU-C (ADO-1216), AC2 — offset-pagination state for a listing.
 *  Lock-step with the backend `NodeListingPageDto`. `page` (1-based) + `totalPages`
 *  drive the "Page x of y" indicator and enable/disable "Previous"/"Next";
 *  `pageSize` is the applied "Per page" window; `total` is the count of ALL
 *  products matching the node + filters. `totalPages` is always ≥ 1. */
export interface NodeListingPage {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

/** A node's product listing plus the breadcrumb trail back to the Department. */
export interface NodeListing {
  /** The node being listed; `null` for the ROOT (all-products) listing, where
   *  the breadcrumb is empty. Lock-step with `NodeListingDto.node`. */
  readonly node: CategoryNode | null;
  /** Department → … → node, root-first, for breadcrumb rendering (AC "breadcrumb"). */
  readonly breadcrumb: readonly CategoryNode[];
  /** Products in this node matching the optional filter (empty for an empty node — AC6).
   *  Story 7-FU-C (ADO-1216): ONE bounded page of the whole set — the client
   *  navigates numbered pages ("Previous"/"Next") and picks the page size
   *  ("Per page"); `page` carries the total so the UI can render "Page x of y". */
  readonly products: readonly ProductSummary[];
  /** Story 7.10 (ADO-906): available attribute facets (key + values + counts) for
   *  this listing, reflecting the other active filters (AC5). Empty when no facet
   *  has a value in the current set. Lock-step with `NodeListingDto.facets`.
   *  Story 7-FU-C (ADO-1216): reflects the WHOLE node (not the fetched page). */
  readonly facets: readonly Facet[];
  /** Story 7.29 (ADO-2025): the catalogue narrowings this listing offers, which the rail renders
   *  and nothing else. Lock-step with `NodeListingDto.narrowings`; the server always sends it, and
   *  an absent value (an older response) offers no narrowing. */
  readonly narrowings?: readonly CatalogueNarrowing[];
  /** Story 7.29 (ADO-2025, D-7.29-25): the offered narrowings with nothing to narrow in scope,
   *  which the rail shows disabled. Lock-step with `NodeListingDto.unavailableNarrowings`. */
  readonly unavailableNarrowings?: readonly CatalogueNarrowing[];
  /** Story 7.29 (ADO-2025, D-7.29-29): the facet selection the listing applied, which the chips
   *  and the active-filter count read. Lock-step with `NodeListingDto.appliedFilters`. */
  readonly appliedFilters?: ProductFilters;
  /** Story 7.29 (ADO-2025, D-7.29-29): whether the listing applied Catering only. */
  readonly cateringOnly?: boolean;
  /** Story 7-FU-C (ADO-1216), AC2: offset-pagination state. Lock-step with
   *  `NodeListingDto.page`. */
  readonly page: NodeListingPage;
  /** Story 7.32 (ADO-1115): the offer kinds a collected-offers listing could not load this
   *  time (today only `FEATURED_LINE`); the other offers are still listed. Absent when none.
   *  Lock-step with `NodeListingDto.unavailableOfferKinds`. */
  readonly unavailableOfferKinds?: readonly OfferKind[];
}

/**
 * Story 8.1 (ADO-2010), Tasks 8.2/8.3 — the product-list SCOPE params the two listing
 * routes accept (`GET /catalogue/products` and `/catalogue/nodes/:id/products`, not
 * search). They AND with the node, the text filter and the facets, and bound the page,
 * the total and the facet counts. Lock-step with the backend `product-scope.dto.ts`.
 *   - `hierarchyCode`: comma-joined department / comgroup / leaf code prefixes (OR);
 *   - `lsnCategory`: one LSN category name, matched exactly;
 *   - `cateringOnly`: catering products only (SL1.0 `p_cater = 'Y'`);
 *   - `globalFavourite`: RESERVED for Story 7.6 (the API rejects it until then);
 *   - `favourites`: Story 7.29 (ADO-2025, D-7.29-35), the caller's own favourites. Set by the
 *     favourites view, never read from the URL, and no chip: the view is the scope.
 */
export interface ProductScope {
  readonly hierarchyCode?: string;
  readonly lsnCategory?: string;
  readonly cateringOnly?: boolean;
  readonly globalFavourite?: string;
  /**
   * Story 7.33 (ADO-2026): one shelf plan's products (`planogram=<id>`), the plan view's listing.
   * Not a landing-tile narrowing: the plan route owns it (`/planograms/:planogramId`), so it is
   * never read from the `/products` URL, never a removable chip and not counted by
   * {@link hasProductScope}.
   */
  readonly planogram?: string;
  readonly favourites?: boolean;
}

/** The no-scope value (a stable reference, so it can sit in effect dependencies). */
export const NO_PRODUCT_SCOPE: ProductScope = Object.freeze({});

const SCOPE_STRING_PARAMS = ['hierarchyCode', 'lsnCategory', 'globalFavourite'] as const;

/**
 * Story 7.35 (ADO-2032): every query-parameter name the product-list SCOPE owns
 * (the Story 8.1 landing scope), so a screen that re-serialises its OWN state
 * (`/products` URL state) can delete and re-write the scope block as a unit
 * without reaching into `appendScopeParams`'s internals.
 */
export const PRODUCT_SCOPE_PARAM_KEYS: readonly string[] = [...SCOPE_STRING_PARAMS, 'cateringOnly'];

/**
 * Story 7.35 (ADO-2032): a node listing asked for a node the server does not have
 * (a syntactically valid but missing/retired category UUID, e.g. a stale
 * bookmark). Distinct from a transport failure so the listing can render an
 * explicit category-not-found state with a route back to the catalogue root,
 * rather than silently falling back to the root listing or showing a generic
 * error (Decision 8). The ROOT listing (`nodeId === null`) never raises this:
 * a 404 there is a real fault, surfaced as the generic load error.
 */
export class NodeNotFoundError extends Error {
  constructor(readonly nodeId: string) {
    super(`Catalogue node not found (${nodeId})`);
    this.name = 'NodeNotFoundError';
  }
}

/** Read the scope out of a query string (the product-list route's URL). */
export function productScopeFromSearch(search: URLSearchParams): ProductScope {
  const scope: { -readonly [K in keyof ProductScope]: ProductScope[K] } = {};
  for (const param of SCOPE_STRING_PARAMS) {
    const value = search.get(param)?.trim();
    if (value !== undefined && value !== '') scope[param] = value;
  }
  if (search.get('cateringOnly') === 'true') scope.cateringOnly = true;
  return scope;
}

/** Whether any scope param is set. */
export function hasProductScope(scope: ProductScope): boolean {
  return (
    scope.cateringOnly === true || SCOPE_STRING_PARAMS.some((param) => scope[param] !== undefined)
  );
}

/** Append the scope to a listing request (only what is set; `cateringOnly` only when true). */
export function appendScopeParams(params: URLSearchParams, scope: ProductScope): void {
  for (const param of SCOPE_STRING_PARAMS) {
    const value = scope[param];
    if (value !== undefined && value !== '') params.set(param, value);
  }
  if (scope.cateringOnly === true) params.set('cateringOnly', 'true');
  if (scope.planogram !== undefined && scope.planogram !== '') {
    params.set('planogram', scope.planogram);
  }
  if (scope.favourites === true) params.set('favourites', 'true');
}

export const HIERARCHY_PATH = `${API_PREFIX}/catalogue/hierarchy`;
/** Epic 7 demo polish (2026-07-17): the ROOT listing (all products, no node). */
export const ALL_PRODUCTS_PATH = `${API_PREFIX}/catalogue/products`;
const NODE_PRODUCTS_PATH = (nodeId: string) =>
  `${API_PREFIX}/catalogue/nodes/${encodeURIComponent(nodeId)}/products`;
/** Story 7.19 AC8 (ADO-1385): the by-SKU read backing the PDP deep link. */
export const PRODUCT_PATH = (sku: string) =>
  `${API_PREFIX}/catalogue/products/${encodeURIComponent(sku)}`;

/**
 * Fetch the children of `parentId`, or the top-level Departments when
 * `parentId` is null. One endpoint serves all three levels (AC1) — the caller
 * drills Department → Category → Sub-Category by re-calling with each selection.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is the selected ordering entry; the tree
 * holds only the nodes with a listing the caller can see in that entry's range.
 */
export async function fetchChildren(
  entry: string,
  parentId: string | null,
): Promise<CategoryChildren> {
  const url =
    parentId === null
      ? HIERARCHY_PATH
      : `${HIERARCHY_PATH}?parentId=${encodeURIComponent(parentId)}`;
  const res = await apiFetch(withEntry(url, entry));
  if (!res.ok) throw new Error(`Failed to load catalogue hierarchy (${res.status})`);
  return (await res.json()) as CategoryChildren;
}

/**
 * Fetch the product listing for a node, plus its breadcrumb and available facets.
 * `filter` is the optional level-appropriate text filter (AC2); `filters` are the
 * Story 7.10 attribute filters (AC1/AC3) serialised as comma-separated camelCase
 * facet params. Omitted means "all products in the node". An empty `products`
 * array is a valid empty node (AC6), never an error.
 *
 * Story 7-FU-C (ADO-1216), AC2: `page` (1-based) and `pageSize` are the numbered-
 * pagination params behind "Previous"/"Next"/"Per page". Omitted (`undefined`)
 * means the server default (page 1 at the default size); when set they are sent as
 * `?page=&pageSize=`. Optional + last so existing callers are unchanged.
 *
 * Story 8.1 (ADO-2010): `scope` is the landing-tile scope ({@link ProductScope}),
 * sent as its own params; omitted means no scope.
 *
 * Story 7.31 (ADO-1113), AC1/AC3: `collectedOffers` puts the listing into the
 * collected-offers union mode (`?offers=all`) — every live, scoped, priced and
 * orderable offer on the active account, one row per on-offer product (no kind
 * duplicates a listing), whatever its kind. It ANDs with the node, the text
 * filter, the facets and the scope, so a department narrowing bounds the union,
 * its page and its facet counts. Omitted (`false`) is the ordinary listing.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is the selected ordering entry; the listing
 * holds that entry's range only. Story 7.29 (ADO-2025), D-7.29-21: `customerAccountCode` is the
 * active Trading Account, which the narrowings' account-type gates read; `null` names none.
 * Story 7.32 (ADO-1115): the same account prices the listing's Featured Lines (the collected-offers
 * mode and the `offerType=FEATURED_LINE` filter), the one its cart and its card prices use.
 */
export async function fetchNodeProducts(
  entry: string,
  nodeId: string | null,
  filter?: string,
  filters: ProductFilters = {},
  page?: number,
  pageSize?: number,
  scope: ProductScope = NO_PRODUCT_SCOPE,
  collectedOffers = false,
  customerAccountCode: string | null = null,
): Promise<NodeListing> {
  const params = new URLSearchParams({ [ENTRY_PARAM]: entry });
  appendCustomerAccountParam(params, customerAccountCode);
  const trimmed = filter?.trim();
  if (trimmed !== undefined && trimmed !== '') params.set('filter', trimmed);
  appendFilterParams(params, filters);
  appendScopeParams(params, scope);
  if (collectedOffers) params.set('offers', 'all');
  if (page !== undefined) params.set('page', String(page));
  if (pageSize !== undefined) params.set('pageSize', String(pageSize));
  // `nodeId === null` = the ROOT listing (all products, no Department selected).
  const base = nodeId === null ? ALL_PRODUCTS_PATH : NODE_PRODUCTS_PATH(nodeId);
  const res = await apiFetch(`${base}?${params.toString()}`);
  if (!res.ok) {
    // Story 7.35 (ADO-2032): a 404 on a NAMED node is a missing/retired category
    // (a stale deep link), surfaced as its own not-found state (Decision 8); a
    // 404 on the root, or any other status, is a generic load failure.
    if (nodeId !== null && res.status === 404) throw new NodeNotFoundError(nodeId);
    throw new Error(`Failed to load products (${res.status})`);
  }
  return (await res.json()) as NodeListing;
}

/**
 * Story 7.19 AC8 (ADO-1385) — fetch ONE product by its SKU, for the Product
 * Detail Page's deep-link and refresh path.
 *
 * `null` means the server answered authoritatively that there is no such product
 * for this tenant and account type (404). Any OTHER non-ok status THROWS. That
 * split is the whole point of the endpoint: the PDP used to resolve through the
 * ranked keyword search and take the exact-SKU row out of a capped result set, so
 * a real product that ranked below the cap was indistinguishable from one that
 * did not exist, and an outage looked the same as both. The caller renders an
 * explicit not-found state for `null` and a retryable error for a throw. That
 * split is preserved VERBATIM below.
 *
 * Story 7.15 (ADO-1372): the response is now a `ProductDetail` — the summary
 * fields plus `ean` / `origin` / `vatRate` and the category `breadcrumb`. This
 * REPLACES the former `fetchProductBySku` rather than sitting beside it
 * (Deviation D-10): both would call the SAME `PRODUCT_PATH(sku)` URL and differ
 * only in how much of one response they declared, so a second function would
 * either duplicate the request or duplicate the type for nothing. Every previous
 * caller still type-checks because `ProductDetail extends ProductSummary`.
 *
 * Enabler E7-C (ADO-2274), D-1 and D-6: `entry` is the selected ordering entry, and
 * the product is read from that entry's range. A product not listed there is a 404
 * (`null`), shown as not found.
 */
export async function fetchProductDetail(
  entry: string,
  sku: string,
): Promise<ProductDetail | null> {
  const res = await apiFetch(withEntry(PRODUCT_PATH(sku), entry));
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load product (${res.status})`);
  return (await res.json()) as ProductDetail;
}
